import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";
import { InlinePlayer } from "../components/InlinePlayer";
import { CommentThread } from "../components/CommentThread";
import { LikeButton } from "../components/LikeButton";

function FeedCard({ v }) {
  const { user } = useAuth();
  const [showNotes, setShowNotes] = useState(false);
  const isMember = user && (user.is_pro || user.role === "admin");
  return (
    <div data-testid={`feed-card-${v.video_id}`} className="border border-white/10 bg-white/[0.02] overflow-hidden flex flex-col">
      <div className="relative aspect-[4/5] bg-black overflow-hidden group">
        <InlinePlayer videoId={v.video_id} testid={`feed-player-${v.video_id}`} />
        <div className="absolute top-3 right-3 overline pointer-events-none">Cut Clinic</div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {mediaUrl(v.author_avatar_url) ? (
            <img src={mediaUrl(v.author_avatar_url)} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-[#050A08]"
                  style={{ background: v.author_avatar || "#C9A227" }}>
              {(v.author_name || "G").slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className="text-xs text-white/80">{v.author_name}</span>
        </div>
        <Link to={`/video/${v.video_id}`} data-testid={`feed-title-${v.video_id}`} className="block">
          <h3 className="font-display text-2xl leading-tight hover:text-[#C9A227] transition">{v.title}</h3>
        </Link>
        <div className="mt-4 flex items-center gap-5">
          <LikeButton videoId={v.video_id} initialCount={v.likes_count} initialLiked={v.liked} testid={`feed-like-${v.video_id}`} />
          <button onClick={() => setShowNotes((s) => !s)} data-testid={`feed-notes-toggle-${v.video_id}`}
                  className="text-xs uppercase tracking-widest text-[#C9A227] hover:underline">
            {showNotes ? "Hide coaching notes" : "View coaching notes"}
          </button>
        </div>
        {showNotes && (
          <div className="mt-3">
            {isMember ? (
              <CommentThread videoId={v.video_id} />
            ) : (
              <div data-testid={`feed-notes-gate-${v.video_id}`}
                   className="border border-[#C9A227]/30 bg-[#C9A227]/[0.06] p-4 text-sm">
                <p className="text-white/85 font-semibold">Coaching notes are a member perk.</p>
                <p className="text-white/60 mt-1">Join Dr. Cuts to read the coach's feedback on every swing.</p>
                <Link to="/pricing" data-testid={`feed-notes-upgrade-${v.video_id}`}
                      className="btn-primary !py-2 !px-4 text-xs mt-3 inline-block">Become a Member · $10/mo</Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Feed() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/videos").then((r) => { setVideos(r.data); setLoading(false); })
      .catch((e) => { console.error("Failed to load feed:", e); setLoading(false); });
  }, []);

  return (
    <div data-testid="feed-page" className="min-h-screen pt-32 pb-24 px-6">
      <Seo title="Swing Feed" description="Browse every member swing and coach response in the Dr. Cuts studio. Real golfers, real cut-shot teardowns." path="/feed" />
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline">The studio</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3">Swing Feed</h1>
            <p className="text-white/60 mt-2">Every member swing. Every coach response.</p>
          </div>
          <Link to="/upload" className="btn-primary hidden sm:inline-block" data-testid="feed-upload-link">Upload Swing</Link>
        </div>

        {loading ? (
          <div className="text-white/50">Loading feed...</div>
        ) : videos.length === 0 ? (
          <div className="p-16 border border-white/10 bg-white/[0.02] text-center" data-testid="feed-empty">
            <h3 className="font-display text-3xl">No swings published yet.</h3>
            <p className="text-white/60 mt-3">Members can publish their swings here once they're ready to share.</p>
            <Link to="/upload" className="btn-primary mt-8 inline-block">Upload Swing</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {videos.map((v) => <FeedCard key={v.video_id} v={v} />)}
          </div>
        )}
      </div>
    </div>
  );
}
